import { type DomainEvent, type DomainEventOf, type DomainEventType } from './event-bus.events';

type Subscriber = (event: DomainEvent) => void;

/**
 * The client event bus (W5) — how one module tells the others that something it
 * owns has changed, without either importing the other (W2/W6).
 *
 * Deliberately not Redux: these are transient notifications with no state to
 * hold, and modelling them as actions would put an entry in the devtools log for
 * every list refresh and tempt someone to reduce over them.
 */
export class EventBus {
  private readonly subscribers = new Set<Subscriber>();

  /** Subscribe to everything. Returns the unsubscribe. */
  subscribe(subscriber: Subscriber): () => void {
    this.subscribers.add(subscriber);
    return () => {
      this.subscribers.delete(subscriber);
    };
  }

  /** Subscribe to one variant, with the payload narrowed to it. */
  subscribeTo<TType extends DomainEventType>(
    type: TType,
    handler: (event: DomainEventOf<TType>) => void,
  ): () => void {
    return this.subscribe((event) => {
      if (event.type === type) {
        handler(event as DomainEventOf<TType>);
      }
    });
  }

  /**
   * Publishes to a SNAPSHOT of the subscriber set.
   *
   * Two things fall out of that, and both are ordinary rather than exotic: a
   * handler may unsubscribe itself while it runs — which is what a component
   * unmounting in response to an event does — and a handler that subscribes
   * during delivery does not receive the event currently being delivered, which
   * would otherwise be a re-entrant loop.
   *
   * A throwing subscriber is logged and skipped. One module's broken handler
   * must not stop another module from invalidating its cache, because the
   * symptom of that is a stale screen with nothing in the console to explain it.
   */
  publish(event: DomainEvent): void {
    for (const subscriber of [...this.subscribers]) {
      try {
        subscriber(event);
      } catch (error) {
        console.error(`EVENT_BUS subscriber failed for "${event.type}"`, error);
      }
    }
  }
}

/** The singleton every module publishes to and subscribes from (W2). */
export const EVENT_BUS = new EventBus();
